import { useDraggable } from '@dnd-kit/core';
import type { CSSProperties } from 'react';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { deriveTechProgress, enRouteEta } from '@/utils/techProgress';
import { formatInTz } from '@/utils/timezone';
import { isMovable, windowLabel, type ScheduleJob } from './scheduleModel';

const PROGRESS_DOT: Record<string, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  'destructive-soft': 'bg-destructive',
  secondary: 'bg-muted-foreground',
  outline: 'bg-muted-foreground',
};

interface ScheduleJobCardProps {
  job: ScheduleJob;
  colour: string;
  timezone: string;
  onOpen: (id: string) => void;
  /** Positioned inside a timeline lane. */
  style?: CSSProperties;
  compact?: boolean;
  overlay?: boolean;
  selected?: boolean;
}

export function ScheduleJobCard({ job, colour, timezone, onOpen, style, compact, overlay, selected }: ScheduleJobCardProps) {
  const movable = isMovable(job);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `job|${job.id}`,
    data: { job },
    disabled: !movable || overlay,
  });

  const progress = deriveTechProgress(job);
  const eta = enRouteEta(job);
  const closed = job.status === 'completed';
  const place = job.customer_suburb || job.customer_postcode || job.customer_address;

  return (
    <button
      type="button"
      ref={overlay ? undefined : setNodeRef}
      {...(overlay ? {} : attributes)}
      {...(overlay ? {} : listeners)}
      onClick={() => onOpen(job.id)}
      style={{ ...style, borderLeftColor: colour }}
      title={[
        job.job_number,
        job.customer_name,
        windowLabel(job),
        job.service_types?.name,
        place,
        progress?.label,
        movable ? null : 'Cannot be moved (on site or closed)',
      ].filter(Boolean).join(' · ')}
      className={cn(
        'group flex min-w-0 flex-col overflow-hidden rounded-md border border-l-4 bg-card text-left shadow-sm transition-colors',
        'hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        compact ? 'px-1.5 py-1' : 'px-2 py-1.5',
        movable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        closed && 'opacity-60',
        progress?.stage === 'declined' && 'border-destructive/60',
        selected && 'ring-2 ring-primary',
        isDragging && 'opacity-30',
        overlay && 'cursor-grabbing shadow-lg ring-2 ring-primary',
      )}
    >
      <span className="flex items-center gap-1.5 text-xs font-semibold leading-tight">
        {progress && (
          <span
            className={cn('h-2 w-2 shrink-0 rounded-full', PROGRESS_DOT[progress.variant] ?? 'bg-muted-foreground')}
            aria-label={progress.label}
          />
        )}
        <span className="truncate">{job.job_number ?? 'Job'}</span>
        <span className="ml-auto shrink-0 font-normal text-muted-foreground">{windowLabel(job)}</span>
        {!movable && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Locked" />}
      </span>
      <span className="truncate text-xs leading-tight">{job.customer_name || 'Unknown customer'}</span>
      {!compact && (eta ? (
        <span className={cn('truncate text-[11px] font-medium leading-tight', eta.late ? 'text-destructive' : 'text-info')}>
          En route · ETA {formatInTz(eta.expectedAt, timezone, 'HH:mm')}{eta.late ? ' (late)' : ''}
        </span>
      ) : (
        <span className="truncate text-[11px] leading-tight text-muted-foreground">
          {[place, job.service_types?.name].filter(Boolean).join(' · ') || ' '}
        </span>
      ))}
    </button>
  );
}
